import { useCallback, useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import type { FlowPlaysRow } from '@shared/flow/plays-view';
import { PLAY_PEOPLE_SECTIONS, PLAY_PEOPLE_SECTION_LABELS, PLAY_PEOPLE_SECTION_COUNT, type PlayPeopleSection } from '@shared/flow/play-people';
import { CampaignDetailSidebar } from '../../../../../../mini-apps/_shared/components/CampaignDetailSidebar';
import { ContactFlowTable } from '../contact/ContactFlowTable';
import { PLAY_SECTION_SURFACE } from '../contact/useContactMount';
import { FlowTable } from '../FlowTable';
import { PlaySectionCard } from './PlaySectionCard';
import { usePlaysListModel } from './usePlaysListModel';

/**
 * `PlaysSubtab` — Command Center's Plays subtab (plan
 * `plans/2026-09-10-001-plays-tab-filter-redesign-plan.md`, amendments
 * **A10–A21**; plan `plans/2026-09-24-005-plays-tab-one-pr-plan.md` U8; plan
 * `plans/2026-09-25-002-plays-sections-unify-accordion-plan.md` U8, which
 * replaces the "People in <Play>" journey below).
 *
 * A11's layout: TWO STACKED SECTIONS, each built on `FlowTable`:
 *
 *   H1 "Plays"   → `<FlowTable<FlowPlaysRow>>` (bound to the Plays saved-views machine, `min-h-[280px] max-h-[380px]`)
 *   32px gap (A19)
 *   below it, ONE of:
 *    - no play selected → H1 "People" + `<ContactFlowTable surface="plays">`
 *    - a play selected  → `<PlayPeopleJourney>`: an H1 "People in <Play>",
 *      an "Expand all"/"Collapse all" toggle and "Back to all people", then
 *      THREE `PlaySectionCard` accordions (R13), each wrapping a
 *      `ContactFlowTable` on the SAME contacts route as All People — one per
 *      "People in <Play>" section (Responded / Not Responded / Not in this
 *      play yet, D6) — each showing its section's defining filter as a
 *      locked, visible row in its own filter bar (`playScope`, U6/D1).
 *
 * Selection, not filtering (D6). A plays-row click SELECTS the play: it
 * lives in the URL as `?play=<id>` (nuqs, `history: 'replace'`, so it
 * survives a reload and deep-links — R19), highlights that row, and swaps
 * the section below. Nothing on the People side is re-filtered, so filters
 * never change a table's shape. Selecting and "Back to all people" touch
 * ONLY `?play=` — never `?view=` (D3): each of the four Contact mounts on
 * this page (the roster, plus the three play sections) owns its own
 * namespaced `?view_<stateKey>=` (or, for the roster, the page-global
 * `?view=`), so nothing can leak between them and there is nothing to clear.
 *
 * The row's "Actions ▾" menu offers ONE item, "Filter for this play" (R12) —
 * it does exactly what a row click does (`selectPlay`). The old three
 * "Go to <section>" items and the `pendingScroll`/`usePlaysJourneyStore`
 * machinery that scrolled a section into view are deleted entirely: with
 * three permanent, always-visible section accordions (default expanded,
 * D8), there is nothing to scroll to that isn't already on the page.
 *
 * Each section accordion's open/closed state is PAGE-LOCAL React state,
 * owned by `PlayPeopleJourney` and reset per play via `key={playId}` on that
 * component (D8) — not a zustand store (this is one page's transient UI
 * state, not module-scope client state) and not persisted across reloads or
 * plays (Scope Boundaries).
 *
 * When `?play=` names a play that is not on the plays table's current page
 * (a deep link, or a play filtered out of the list), the header's name comes
 * from a `GET /api/tools/launcher/campaigns/:id` fetch (D9) — the SAME call
 * the "View Details"/"Manage Event" sidebar already makes — rather than the
 * now-deleted members route's `play` envelope. Play type falls back to
 * `DEFAULT_PLAY_TYPE` ('event_invite', the only registered membership type
 * today).
 *
 * The PLAYS list runs on the saved-views machine (plan 2026-11-03-001 U6):
 * entity `play`, the "Upcoming plays" preset, view-state key `play`, URL
 * param `?view_play=`. Its working state (`filters`, columns, group key,
 * sort) narrows the plays table via `GET /api/flow/plays?filters=…` (A2/U9).
 * The bound `FlowTable` renders the machine's `ViewSwitcher` and dirty box
 * ("Modified" / Reset / Save). It is independent of every table below it;
 * each of those owns its own view-state row.
 *
 * `plans/2026-09-24-005-plays-tab-one-pr-plan.md` U8 — an event row's
 * "View Details"/"Manage Event" menu items (`PlayRowActionsMenu`) open the
 * `_shared` `CampaignDetailSidebar` IN-PAGE, hosted here via `detailPanel`
 * (`{ campaignId, mode } | null`). `mode === 'edit'` is only ever reachable
 * for `isMarketingUser || isAdmin` (the menu doesn't render "Manage Event"
 * otherwise), and is also the client-side `readOnly={mode === 'view'}` guard
 * on the sidebar itself — the server's `marketing_user` role check on
 * `PATCH …/details` (U7) is the REAL gate; this is only about which form the
 * rep sees, never the last line of defense.
 *
 * The campaign fetch/save go through `wrapThrowingApiFetch` over core
 * Terra's own `apiFetch` (`useApi()`), not `apiFetch` raw: core's `apiFetch`
 * resolves with the Response for every non-refusal status (`lib/api.ts`), while
 * `patchCampaignDetails`/`fetchLauncherCampaign` (`_shared/launcher-campaign-
 * schema.ts`) are written for the mini-app boundary's THROWING `apiFetch`
 * (`_shared/hooks/useApi.ts`) — they parse the response body unconditionally, with
 * no `res.ok` check of their own. Without the wrapper, a PATCH 403 would
 * resolve normally with the `problem()` body as its "success" value instead
 * of rejecting, and the sidebar's error `Alert` would never fire.
 * `wrapThrowingApiFetch` throws a `MiniAppFetchError` whose `.message` is the
 * server's `detail` (via `throwFetchError`'s `userMessage` chain), which is
 * exactly what `CampaignDetailSidebar.handleSave`'s `setError(err?.message)`
 * renders — so the Alert shows the server's own text, not a wrapped code.
 *
 * Imports: this component DOES import from `mini-apps/_shared/**` (the U8
 * sidebar, its campaign schema, and the fetch-error wrapper above) — plain
 * shared modules, not a mini-app's own tree. It does not import
 * `EnrollQueueContext.tsx`: hosts mount it (`mini-apps/command-center/
 * index.tsx`, `?tab=plays`) and supply the providers `ContactFlowTable`
 * needs, exactly as they already do for the Contacts subtab. See the
 * component test for the minimum provider set.
 */

/** A6 — both section headings use the People tab's own H1 style, verbatim. */
const SECTION_HEADING_CLS = 'text-lg font-semibold text-content-primary dark:text-nightshade-100';

/**
 * A19 — 32px between the bottom of the plays table and the People H1,
 * replacing A14's 128px. QA round 3 read the larger gap as dead space rather
 * than as a section break; with A18's `min-h-[280px]` giving the plays card a
 * floor, the two sections already separate visually without it.
 */
const PEOPLE_SECTION_CLS = 'mt-8 flex flex-col gap-2 min-h-0';

/** U8 (plan 2026-09-25-002) — the selected-play journey stacks its header and three section accordions with a little more air than a single table. */
const JOURNEY_SECTION_CLS = 'mt-8 flex flex-col gap-6 min-h-0';

/** All-expanded — `PlayPeopleJourney`'s initial/reset accordion state (D8). */
function allExpanded(): Record<PlayPeopleSection, boolean> {
  return { responded: true, not_responded: true, not_in_play: true };
}

interface PlayPeopleJourneyProps {
  playId: string;
  playType: string;
  playName: string;
  /** The selected play's own row counts, when it's on the plays table's current page — `undefined` for a deep link the page hasn't loaded (D9). */
  counts?: FlowPlaysRow['counts'];
  onBack: () => void;
}

/**
 * The selected-play journey (D8/R13): three `PlaySectionCard` accordions,
 * each wrapping a `ContactFlowTable` scoped to one "People in <Play>"
 * section. Rendered with `key={playId}` by the caller, so a fresh
 * `useState` here resets every card to expanded on every play switch — no
 * lifetime ref, trivially StrictMode-safe.
 */
function PlayPeopleJourney({ playId, playType, playName, counts, onBack }: PlayPeopleJourneyProps) {
  const [open, setOpen] = useState<Record<PlayPeopleSection, boolean>>(allExpanded);
  const allOpen = PLAY_PEOPLE_SECTIONS.every(section => open[section]);

  const toggleAll = useCallback(() => {
    const next = !allOpen;
    setOpen({ responded: next, not_responded: next, not_in_play: next });
  }, [allOpen]);

  const setSectionOpen = useCallback((section: PlayPeopleSection, sectionOpen: boolean) => {
    setOpen(prev => ({ ...prev, [section]: sectionOpen }));
  }, []);

  return (
    <div data-testid="play-people-journey" className={JOURNEY_SECTION_CLS}>
      <header className="flex items-center justify-between gap-3">
        <h1 className={SECTION_HEADING_CLS}>People in {playName}</h1>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={toggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onBack}>
            Back to all people
          </Button>
        </div>
      </header>

      {PLAY_PEOPLE_SECTIONS.map(section => {
        const { key, suffix } = PLAY_PEOPLE_SECTION_COUNT[section];
        return (
          <PlaySectionCard
            key={section}
            data-testid={`play-section-${section}`}
            title={PLAY_PEOPLE_SECTION_LABELS[section]}
            count={counts?.[key]}
            countSuffix={suffix}
            open={open[section]}
            onOpenChange={sectionOpen => setSectionOpen(section, sectionOpen)}
          >
            <ContactFlowTable surface={PLAY_SECTION_SURFACE[section]} playScope={{ playId, playType, section }} />
          </PlaySectionCard>
        );
      })}
    </div>
  );
}

/** V1's plays-list empty state (review F9 — was `usePlaysListModel`'s). */
const PLAYS_EMPTY_STATE = (
  <>
    <div className="font-medium text-content-secondary dark:text-nightshade-400">No plays match</div>
    <div className="text-content-tertiary dark:text-nightshade-500">Remove a filter to widen the list.</div>
  </>
);

export function PlaysSubtab() {
  const m = usePlaysListModel();
  // Page-local roster accordion state (V1 has no page-level toggle). A closed
  // roster unmounts `ContactFlowTable`; its working state survives via the
  // `contact_play` view-state row (same contract as the journey sections).
  const [rosterOpen, setRosterOpen] = useState(true);

  return (
    <div className="flex flex-col">
      <div data-testid="plays-section" className="flex flex-col gap-2">
        <h1 className={SECTION_HEADING_CLS}>Plays</h1>

        <FlowTable<FlowPlaysRow>
          {...m.tableProps}
          // Review F9 — moved here verbatim from `usePlaysListModel`: this is
          // V1's own markup and must not reach a V2 ledger (`dark:` +
          // `nightshade` are banned under `shell-next/`). Points at the
          // Filters POPOVER, not at a chip strip — A12 deleted the strip, so
          // "remove a chip above" would name a control that no longer exists.
          emptyState={PLAYS_EMPTY_STATE}
        />
      </div>

      {m.playId === null ? (
        <div data-testid="people-section" className={PEOPLE_SECTION_CLS}>
          <PlaySectionCard title="People" asPageHeading titleClassName={SECTION_HEADING_CLS} open={rosterOpen} onOpenChange={setRosterOpen}>
            <ContactFlowTable surface="plays" />
          </PlaySectionCard>
        </div>
      ) : (
        <PlayPeopleJourney
          key={m.playId}
          playId={m.playId}
          playType={m.playType}
          playName={m.playName}
          counts={m.selectedPlay?.counts}
          onBack={m.backToAllPeople}
        />
      )}

      {m.detail.panel && m.detail.campaign && (
        <CampaignDetailSidebar
          campaign={m.detail.campaign}
          readOnly={m.detail.panel.mode === 'view'}
          onClose={m.detail.close}
          onSave={m.detail.save}
        />
      )}
    </div>
  );
}
